import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, MapPin, Clock, CheckCircle2, XCircle, CalendarClock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/States";
import { api, formatError } from "@/lib/api";

export const STATUS_STYLE = { new: "bg-elevated text-ink-2", shortlisted: "bg-blue-50 text-blue-700", rejected: "bg-red-50 text-red-700", hired: "bg-emerald-100 text-emerald-800", withdrawn: "bg-elevated text-ink-3" };
export const STATUS_LABEL = { new: "Under review", shortlisted: "Shortlisted", rejected: "Not selected", hired: "Hired", withdrawn: "Withdrawn" };
const MODE = { onsite: "On-site", hybrid: "Hybrid", remote: "Remote" };

export function JobMeta({ j }) {
  return (
    <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-3">
      <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" />{j.company_slug ? <Link to={`/company/${j.company_slug}`} data-testid={`job-company-link-${j.id}`} className="font-medium text-aqua hover:underline">{j.company}</Link> : j.company}</span>
      <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{j.location.toLowerCase() === j.work_mode ? MODE[j.work_mode] : `${j.location} · ${MODE[j.work_mode]}`}</span>
      <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{j.experience}</span>
      {j.deadline && <span className="inline-flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Apply by {new Date(`${j.deadline}T00:00:00`).toLocaleDateString()}</span>}
    </p>
  );
}

function Eligibility({ j }) {
  if (!j.requirements.length) return <p data-testid={`job-eligible-${j.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" />You're eligible · open to all learners</p>;
  return j.eligible ? (
    <p data-testid={`job-eligible-${j.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700"><CheckCircle2 className="h-4 w-4" />You're eligible · {j.matched.join(", ")}</p>
  ) : (
    <p data-testid={`job-missing-${j.id}`} className="inline-flex items-start gap-1.5 text-sm font-medium text-amber-700"><XCircle className="mt-0.5 h-4 w-4 shrink-0" />Missing: {j.missing.join(" or ")} certificate</p>
  );
}

function JobCard({ j, onApply }) {
  const [more, setMore] = useState(false);
  return (
    <div data-testid={`job-${j.id}`} className="card-hover rounded-2xl border border-line bg-surface p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-ink">{j.title}</h3>
          <JobMeta j={j} />
        </div>
        {j.applied_status ? (
          <span data-testid={`job-applied-${j.id}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[j.applied_status]}`}>Applied · {STATUS_LABEL[j.applied_status]}</span>
        ) : (
          <Button data-testid={`job-apply-btn-${j.id}`} disabled={!j.eligible} onClick={() => onApply(j)} className="rounded-full bg-solid">Apply</Button>
        )}
      </div>
      <p className={`mt-4 whitespace-pre-wrap text-sm leading-relaxed text-ink-2 ${more ? "" : "line-clamp-3"}`}>{j.description}</p>
      {j.description.length > 240 && <button data-testid={`job-more-${j.id}`} onClick={() => setMore(!more)} className="mt-1 text-xs font-medium text-aqua hover:underline">{more ? "Show less" : "Read more"}</button>}
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4">
        {j.requirements.length > 0 && <span className="text-xs text-ink-3">Requires one of:</span>}
        {j.requirements.map((r) => <span key={`${r.kind}-${r.id}`} className="rounded-full bg-aqua-soft px-2.5 py-0.5 text-xs font-medium text-aqua">{r.label}</span>)}
      </div>
      <div className="mt-3"><Eligibility j={j} /></div>
    </div>
  );
}

function ApplyDialog({ job, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/jobs/${job.id}/apply`, { cover_note: note }); toast.success("Application sent"); setNote(""); onDone(); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!job} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="apply-dialog" className="w-[calc(100%-2rem)] rounded-2xl sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-ink">Apply: {job?.title}</DialogTitle>
          <DialogDescription>Your verified IDDCR certificates and public showcase link (if turned on) are attached automatically.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="apply-cover-note" rows={5} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional cover note: why you're a good fit..." />
        <div className="flex items-center justify-between">
          <span data-testid="apply-cover-count" className="text-xs text-ink-3">{note.length}/1000</span>
          <Button data-testid="apply-submit-btn" disabled={busy} onClick={submit} className="rounded-full bg-solid">{busy ? "Sending..." : "Send application"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WithdrawDialog({ app, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/jobs/applications/${app.id}/withdraw`, { reason }); toast.success("Application withdrawn"); setReason(""); onDone(); } catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!app} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="withdraw-dialog" className="w-[calc(100%-2rem)] rounded-2xl sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-ink">Withdraw application?</DialogTitle>
          <DialogDescription>You're withdrawing from <b>{app?.job_title}</b> at {app?.company}. You won't be able to apply for this job again.</DialogDescription>
        </DialogHeader>
        <Textarea data-testid="withdraw-reason" rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional), e.g. accepted another offer" />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" data-testid="withdraw-cancel-btn" onClick={onClose} className="rounded-full">Keep application</Button>
          <Button data-testid="withdraw-confirm-btn" disabled={busy} onClick={submit} className="rounded-full bg-red-600 hover:bg-red-700">{busy ? "Withdrawing..." : "Withdraw"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MyApplications({ items, onWithdraw }) {
  if (!items.length) return <EmptyState testId="applications-empty" title="No applications yet" text="Jobs you apply for will appear here with their status." />;
  return (
    <div data-testid="applications-list" className="flex flex-col gap-3">
      {items.map((a) => (
        <div key={a.id} data-testid={`application-${a.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-surface p-5">
          <div>
            <p className="font-semibold text-ink">{a.job_title}</p>
            <p className="text-sm text-ink-3">{a.company} · applied {new Date(a.created_at).toLocaleDateString()} · {a.certificates.length} certificate{a.certificates.length === 1 ? "" : "s"} attached</p>
            {a.showcase_url && <a href={a.showcase_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-aqua hover:underline"><ExternalLink className="h-3 w-3" />Showcase attached</a>}
          </div>
          <div className="flex items-center gap-2">
            <span data-testid={`application-status-${a.id}`} className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_STYLE[a.status]}`}>{STATUS_LABEL[a.status]}</span>
            {["new", "shortlisted"].includes(a.status) && <Button size="sm" variant="ghost" data-testid={`application-withdraw-${a.id}`} onClick={() => onWithdraw(a)} className="rounded-full text-red-600 hover:text-red-700">Withdraw</Button>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function Jobs() {
  const [jobs, setJobs] = useState(null);
  const [apps, setApps] = useState([]);
  const [err, setErr] = useState("");
  const [applying, setApplying] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const load = () => Promise.all([api.get("/jobs"), api.get("/jobs/applications/mine")])
    .then(([j, a]) => { setJobs(j.data); setApps(a.data); }).catch((e) => setErr(formatError(e)));
  useEffect(() => { load(); }, []);
  if (err) return <ErrorState message={err} />;
  if (!jobs) return <Loading />;
  return (
    <div>
      <PageHeader eyebrow="Careers" title="Clinical research jobs" text="Openings from IDDCR's placement team and approved partner employers, all reviewed by IDDCR. Hold one of the listed certificates to apply." />
      <Tabs defaultValue="open">
        <TabsList className="mb-6">
          <TabsTrigger value="open" data-testid="jobs-tab-open">Open jobs ({jobs.length})</TabsTrigger>
          <TabsTrigger value="mine" data-testid="jobs-tab-mine">My applications ({apps.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="open">
          {jobs.length === 0 ? <EmptyState testId="jobs-empty" title="No open jobs right now" text="New clinical research roles are posted regularly. Keep earning certificates!" /> : (
            <div data-testid="jobs-list" className="flex flex-col gap-4">{jobs.map((j) => <JobCard key={j.id} j={j} onApply={setApplying} />)}</div>
          )}
        </TabsContent>
        <TabsContent value="mine"><MyApplications items={apps} onWithdraw={setWithdrawing} /></TabsContent>
      </Tabs>
      <WithdrawDialog app={withdrawing} onClose={() => setWithdrawing(null)} onDone={() => { setWithdrawing(null); load(); }} />
      <ApplyDialog job={applying} onClose={() => setApplying(null)} onDone={() => { setApplying(null); load(); }} />
    </div>
  );
}
